/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\aiAgentsDocumentsService.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/


import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { ContextDocument } from './aiAgentsTypes';

const BASE_PATH = '/api/telephony/ai-agents';

export async function getContextDocuments(agentId: number | string): Promise<ContextDocument[]> {
    const res = await fetch(buildApiUrl(`${BASE_PATH}/${agentId}/documents`), { method: 'GET', headers: getApiHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return (await res.json()).data || [];
}

export async function uploadContextDocument(agentId: number | string, file: File, extractionPrompt?: string): Promise<ContextDocument> {
    const fd = new FormData();
    fd.append('file', file);
    if (extractionPrompt) fd.append('extractionPrompt', extractionPrompt);
    const headers = getApiHeaders();
    delete (headers as any)['Content-Type']; // Let browser set multipart boundary
    const res = await fetch(buildApiUrl(`${BASE_PATH}/${agentId}/documents`), { method: 'POST', headers, body: fd });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || `HTTP ${res.status}`); }
    return (await res.json()).data;
}

export async function processContextDocument(
    agentId: number | string, documentId: number | string,
    aiProfileId: number | string, customPrompt?: string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<{ contextsExtracted: number; contexts: any[] }> {
    const res = await fetch(buildApiUrl(`${BASE_PATH}/${agentId}/documents/${documentId}/process`), { method: 'POST', headers: getApiHeaders(), body: JSON.stringify({ aiProfileId, customPrompt }) });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || `HTTP ${res.status}`); }
    return (await res.json()).data;
}

export async function updateContextDocument(agentId: number | string, documentId: number | string, data: { extractionPrompt?: string }): Promise<ContextDocument> {
    const res = await fetch(buildApiUrl(`${BASE_PATH}/${agentId}/documents/${documentId}`), { method: 'PUT', headers: getApiHeaders(), body: JSON.stringify(data) });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || `HTTP ${res.status}`); }
    return (await res.json()).data;
}

export async function deleteContextDocument(agentId: number | string, documentId: number | string): Promise<void> {
    const res = await fetch(buildApiUrl(`${BASE_PATH}/${agentId}/documents/${documentId}`), { method: 'DELETE', headers: getApiHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
}

export async function getDefaultExtractionPrompt(): Promise<string> {
    const res = await fetch(buildApiUrl(`${BASE_PATH}/extraction-prompt`), { method: 'GET', headers: getApiHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return (await res.json()).data?.prompt || '';
}
